import {
  Box,
  Input as ChakraInput,
  InputProps as ChakraInputProps,
  FormLabel,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { Control, Controller, FieldError } from 'react-hook-form';
import ptBr from 'date-fns/locale/pt-BR';

import 'react-datepicker/dist/react-datepicker.css';

interface IDatePickerProps extends ChakraInputProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any, any>;
  error?: FieldError;
  excludeDates?: Date[];
  hideTodayButton?: boolean;
  includeDates?: Date[];
  isClearable?: boolean;
  label?: string;
  maxDate?: Date;
  minDate?: Date;
  startDate?: Date;
  endDate?: Date;
  name: string;
  selectsStart?: boolean;
  selectsEnd?: boolean;
  size?: string;
  onMonthChange?(date: Date): void;
  onFocus?(): void;
}

registerLocale('pt-BR', ptBr);

const DatePickerBase = ({
  name,
  label,
  control,
  error,
  excludeDates,
  hideTodayButton = false,
  includeDates,
  isClearable = true,
  maxDate,
  isDisabled = false,
  isRequired,
  minDate,
  startDate,
  endDate,
  selectsEnd = false,
  selectsStart = false,
  onMonthChange,
  onFocus,
  placeholder,
  size = 'lg',
  zIndex = 3,
  ...rest
}: IDatePickerProps): JSX.Element => (
  <FormControl
    isInvalid={!!error}
    display="flex"
    flexDirection="column"
    zIndex={zIndex}
    isDisabled={isDisabled}
  >
    {!!label && (
      <FormLabel htmlFor={name} data-disabled={isDisabled ? '' : undefined}>
        {label}
        {isRequired && (
          <Box
            as="span"
            role="presentation"
            aria-hidden
            marginStart={1}
            color="red.500"
          >
            *
          </Box>
        )}
      </FormLabel>
    )}

    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, onBlur, value } }) => (
        <ReactDatePicker
          dateFormat="dd/MM/yyyy"
          excludeDates={excludeDates}
          includeDates={includeDates}
          isClearable={isClearable}
          disabled={isDisabled}
          locale="pt-BR"
          maxDate={maxDate}
          minDate={minDate}
          startDate={startDate}
          endDate={endDate}
          onBlur={onBlur}
          onChange={onChange}
          onFocus={onFocus}
          onMonthChange={onMonthChange}
          placeholderText={placeholder}
          selectsEnd={selectsEnd}
          selectsStart={selectsStart}
          selected={value}
          todayButton={!hideTodayButton ? 'Hoje' : undefined}
          customInput={
            <ChakraInput
              _disabled={{
                background: 'gray.100',
                borderColor: 'gray.300',
                color: '#99999a',
                opacity: 0.8,
                cursor: 'not-allowed',
              }}
              borderColor="gray.300"
              bg="gray.100"
              focusBorderColor="blue.300"
              onKeyUp={(e) => {
                e.currentTarget.maxLength = 10;

                e.currentTarget.value = e.currentTarget.value
                  .replace(/\D/g, '')
                  .replace(/(\d{2})(\d)/, '$1/$2')
                  .replace(/(\d{2})(\d)/, '$1/$2');
              }}
              size={size}
              variant="outline"
            />
          }
        />
      )}
      {...rest}
    />

    {!!error && (
      <FormErrorMessage position="absolute" bottom={-7}>
        {error.message}
      </FormErrorMessage>
    )}
  </FormControl>
);

export const DatePicker = DatePickerBase;
